import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Download } from '@factorialco/f0-react/icons/app'
import {
  BenefitsValueObjectsBenefitMembersMemberTypeEnum,
  BenefitsValueObjectsBenefitStatusEnum,
  HealthcareManagementPageDataCollectionDocument,
  HealthcareManagementPageDataCollectionQuery,
  HealthcareManagementPageEnrollmentFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  getRelationshipLabel,
  getStatusLabel,
  getStatusVariant,
} from 'modules/benefits/pages/HealthcareManagementPage/lib/enrollmentHelpers'

interface EnrollmentsTableProps {
  companyId: number
}

export const EnrollmentsTable = ({ companyId }: EnrollmentsTableProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const dataSourceOptions = useMemo(() => {
    return {
      document: HealthcareManagementPageDataCollectionDocument,
      fetchPolicy: 'cache-and-network' as const,
      mapData: (data: HealthcareManagementPageDataCollectionQuery) => {
        return data.benefits.healthcareEnrollmentsConnection.edges.map((edge) => edge.node)
      },
      mapPagination: (data: HealthcareManagementPageDataCollectionQuery) => {
        const count = data.benefits.healthcareEnrollmentsConnection.totalCount ?? 0
        return { total: count }
      },
      mapVariables: ({
        search,
        sortings,
        filters,
      }: {
        search?: string
        sortings?: Array<{ field: string; order: 'asc' | 'desc' }>
        filters?: {
          memberTypes?: BenefitsValueObjectsBenefitMembersMemberTypeEnum[]
          statuses?: BenefitsValueObjectsBenefitStatusEnum[]
        }
      }) => {
        return {
          companyId,
          sortOrder: sortings?.[0],
          search,
          memberTypes: filters?.memberTypes,
          statuses: filters?.statuses,
        }
      },
      search: {
        enabled: true,
      },
      sortings: {
        effectiveDate: {
          label: i18n.t('benefits.healthcare.management.columns.active_date'),
        },
        firstName: {
          label: i18n.t('benefits.healthcare.management.columns.member_name'),
        },
        taxId: {
          label: i18n.t('benefits.healthcare.management.columns.tax_id'),
        },
        email: {
          label: i18n.t('benefits.healthcare.management.columns.contact'),
        },
      },
      defaultSortings: {
        field: 'firstName' as const,
        order: 'asc' as const,
      },
      primaryActions: () => ({
        label: i18n.t('common.import_only'),
        onClick: () => navigate(navigation.benefits.catalog.healthcare.importer.fullPath()),
        icon: Download,
      }),
      filters: {
        memberTypes: {
          type: 'in' as const,
          label: i18n.t('benefits.healthcare.management.filters.member_type.label'),
          options: {
            options: async (): Promise<
              Array<{
                label: string
                value: BenefitsValueObjectsBenefitMembersMemberTypeEnum
              }>
            > => [
              {
                label: i18n.t(
                  'benefits.healthcare.management.filters.member_type.options.employee'
                ),
                value: 'employee',
              },
              {
                label: i18n.t('benefits.healthcare.management.filters.member_type.options.spouse'),
                value: 'spouse',
              },
              {
                label: i18n.t('benefits.healthcare.management.filters.member_type.options.child'),
                value: 'child',
              },
              {
                label: i18n.t('benefits.healthcare.management.filters.member_type.options.partner'),
                value: 'partner',
              },
              {
                label: i18n.t(
                  'benefits.healthcare.management.filters.member_type.options.other_family'
                ),
                value: 'other_family',
              },
              {
                label: i18n.t(
                  'benefits.healthcare.management.filters.member_type.options.other_non_family'
                ),
                value: 'other_non_family',
              },
            ],
          },
        },
        statuses: {
          type: 'in' as const,
          label: i18n.t('benefits.healthcare.management.filters.status.label'),
          options: {
            options: async (): Promise<
              Array<{ label: string; value: BenefitsValueObjectsBenefitStatusEnum }>
            > => [
              {
                label: i18n.t('benefits.healthcare.enrollment.status.pending'),
                value: 'pending',
              },
              {
                label: i18n.t('benefits.healthcare.enrollment.status.requested'),
                value: 'requested',
              },
              {
                label: i18n.t('benefits.healthcare.enrollment.status.active'),
                value: 'active',
              },
              {
                label: i18n.t('benefits.healthcare.enrollment.status.inactive'),
                value: 'inactive',
              },
            ],
          },
        },
      },
      presets: [
        {
          label: i18n.t('benefits.healthcare.management.presets.active_enrollments'),
          filter: {
            statuses: ['active'] satisfies BenefitsValueObjectsBenefitStatusEnum[],
          },
        },
        {
          label: i18n.t('benefits.healthcare.management.presets.pending_approvals'),
          filter: {
            statuses: ['pending'] satisfies BenefitsValueObjectsBenefitStatusEnum[],
          },
        },
        {
          label: i18n.t('benefits.healthcare.management.presets.employees_only'),
          filter: {
            memberTypes: ['employee'] satisfies BenefitsValueObjectsBenefitMembersMemberTypeEnum[],
          },
        },
        {
          label: i18n.t('benefits.healthcare.management.presets.dependents_only'),
          filter: {
            memberTypes: [
              'spouse',
              'child',
              'partner',
              'other_family',
              'other_non_family',
            ] satisfies BenefitsValueObjectsBenefitMembersMemberTypeEnum[],
          },
        },
      ],
    }
  }, [companyId])

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [companyId])

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('benefits.healthcare.management.columns.member_name'),
                sorting: 'firstName',
                render: (item: HealthcareManagementPageEnrollmentFragment) => {
                  return {
                    type: 'text',
                    value: `${item.firstName} ${item.lastName}`,
                  }
                },
              },
              {
                label: i18n.t('benefits.healthcare.management.columns.tax_id'),
                sorting: 'taxId',
                render: (item: HealthcareManagementPageEnrollmentFragment) => {
                  return {
                    type: 'text',
                    value: item.taxId || '-',
                  }
                },
              },
              {
                label: i18n.t('benefits.healthcare.management.columns.relationship'),
                render: (item: HealthcareManagementPageEnrollmentFragment) => {
                  return {
                    type: 'text',
                    value: getRelationshipLabel(item.relationshipToEmployee),
                  }
                },
              },
              {
                label: i18n.t('benefits.healthcare.management.columns.active_date'),
                sorting: 'effectiveDate',
                render: (item: HealthcareManagementPageEnrollmentFragment) => {
                  return {
                    type: 'date',
                    value: item.effectiveDate
                      ? i18nDateToString(new Date(item.effectiveDate), 'ui')
                      : '-',
                  }
                },
              },
              {
                label: i18n.t('benefits.healthcare.management.columns.status'),
                render: (item: HealthcareManagementPageEnrollmentFragment) => {
                  if (!item.status) {
                    return { type: 'text', value: '-' }
                  }

                  return {
                    type: 'status',
                    value: {
                      label: getStatusLabel(item.status),
                      status: getStatusVariant(item.status),
                    },
                  }
                },
              },
              {
                label: i18n.t('benefits.healthcare.management.columns.contact'),
                sorting: 'email',
                render: (item: HealthcareManagementPageEnrollmentFragment) => {
                  return {
                    type: 'text',
                    value: item.email || item.phoneNumber || '-',
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
